import { FC, useMemo, useCallback, useState, useRef, useEffect, MutableRefObject } from 'react'
import Image, { StaticImageData } from "next/legacy/image";
import { styled } from "@mui/material/styles";
import { throttle } from "throttle-debounce";
import { PointsBrewingInfo } from "@/types/points"
import { formatThousand } from "@/utils/util"

import MallArrowIcon from "@/public/img/points/mall-arrow.svg"

interface Props {
  className?: string
  info: PointsBrewingInfo
  bucketSource: StaticImageData
  bucketLightSource: StaticImageData
  containerRef?: MutableRefObject<HTMLDivElement>
  driection?: 'top' | 'bottom' | 'left' | 'right'
  offset?: number
  onShow?: () => void
  onHide?: () => void
  openModal?: (info: PointsBrewingInfo) => void
}

/**
 * 酿造酒桶hover态
 */
const _HoverTip: FC<Props> = ({ children, className, containerRef, info, driection, offset, onShow, onHide, bucketSource, bucketLightSource, openModal }) => {
  const wrapRef = useRef<HTMLDivElement>()
  const popoverRef = useRef<HTMLDivElement>()
  const [rect, setRect] = useState<{ left: number; top: number; width: number; height: number; }>()
  const [popoverRect, setPopoverRect] = useState<DOMRect>()
  const [show, setShow] = useState(false)

  const _driection = driection ?? 'top'
  // position check
  const position = useMemo(() => {
    if (!rect || !popoverRect) return {}
    const _style: Record<string, string> = {}
    const pre = ['top', 'left'].includes(_driection) ? -1 : 1
    if (['top', 'bottom'].includes(_driection)) {
      _style.left = `${rect.left - ((popoverRect.width - rect.width) / 2)}px`
      // _style.left = `${rect.left}px`
      const num = (_driection === 'bottom' ? rect.height : (-popoverRect.height))
      _style.top = `${rect.top + num + (pre * (offset ?? 0))}px`
      console.log('- _style', _style, popoverRect, rect)
    } else {
      const num = (_driection === 'left' ? -popoverRect.width : rect.width)
      _style.left = `${rect.left + num + (pre * (offset ?? 0))}px`
      _style.top = `${rect.top + (rect.height - popoverRect.height) / 2}px`
    }
    return _style
  }, [_driection, offset, rect, popoverRect])

  const mouseOverHandle = () => {
    setShow(true)
    onShow?.()
  }

  const mouseOutHandle = () => {
    setShow(false)
    onHide?.()
  }

  // 计算容器的偏移距离
  const calcRect = useCallback(() => {
    if (!wrapRef.current || !containerRef?.current) return
      const containerRect = containerRef?.current?.getBoundingClientRect()
      const rect = wrapRef.current.getBoundingClientRect()
      const left = rect.x - containerRect.x
      const top = rect.y - containerRect.y
      const _rect = { left, top, width: rect.width, height: rect.height }
      setRect(() => _rect)
  }, [wrapRef, containerRef])

  // mounted
  useEffect(() => {
    if (!wrapRef.current || !containerRef?.current || !popoverRef?.current) return
    calcRect()
    setPopoverRect(popoverRef?.current.getBoundingClientRect())

    containerRef?.current.appendChild(popoverRef.current)
  }, [wrapRef, containerRef, popoverRef, calcRect])

  // resize: reset rect
  useEffect(() => {
    const resizeHandle = throttle(1000, calcRect)
    window.addEventListener('resize', resizeHandle)
    return () => {
      window.removeEventListener('resize', resizeHandle)
    }
  }, [calcRect])

  return (
    <div
      ref={wrapRef}
      className={`relative w-full h-full bucket-hover-wrap cursor-pointer ${show && 'active'} ${className}`}
      onMouseOverCapture={mouseOverHandle}
      onMouseOutCapture={mouseOutHandle}
      onClick={openModal.bind(this, info)}>
      <Image alt="tarven-bucket" src={bucketSource} layout="fill" className="pointer-events-none absolute bucket-item bucket-normal"/>
      <Image alt="tarven-bucket" src={bucketLightSource} layout="fill" className="pointer-events-none absolute bucket-item bucket-light"/>
      <div
        style={position}
        ref={popoverRef}
        className={`absolute py-[5.5px] px-[5.5px] w-[278px] cursor-pointer border-solid border-[2.5px] border-black bg-white rounded-[24px] bucket-popover ${!show && 'invisible'} z-[99]`}>
          <div className="w-full h-full flex items-center pointer-events-auto bucket-popover-cntr">
            <div className="w-[80px] h-[80px] rounded-[16px] overflow-hidden">
              <nft-viewer
                type="image"
                src={ info.tokenImage }/>
            </div>
            <div className="ml-[16px] flex-1">
              <p className="m-0 mb-[12px] font-medium text-[14px] leading-[16px] text-black">Token #{info.token}</p>
              <p className="m-0 h-0 border-0 border-t-[1px] scale-y-50 border-solid border-[#E3E5E7]"></p>
              <div className="mt-[10px] flex items-baseline font-black text-black">
                <h3 className="m-0 text-[18px] leading-[21px]">{ formatThousand(info.income) }</h3>
                <p className="m-0 ml-[4px] text-[12px] leading-[14px] font-medium">DAYS</p>
              </div>
            </div>
            <MallArrowIcon className="ml-[30px] mr-[20px]"/>
          </div>
      </div>
    </div>
  )
}

const HoverTip = styled(_HoverTip)`
  &.bucket-hover-wrap {
    .bucket-item {
      top: 0;
      left: 0;
    }
    .bucket-normal {
      opacity: 1;
    }
    .bucket-light {
      opacity: 0;
    }
    &.active {
      .bucket-normal {
        opacity: 0;
      }
      .bucket-light {
        opacity: 1;
      }
    }
    &:hover {
      .bucket-normal {
        opacity: 0;
      }
      .bucket-light {
        opacity: 1;
      }
    }
  }
`

export default HoverTip
